import { useBoolean } from 'usehooks-ts';
import { useMemo, useState } from 'react';

import { styled } from '@mui/material';
import Stack from '@mui/material/Stack';
import Input from '@mui/material/Input';
import Grid2 from '@mui/material/Grid2';
import Slider from '@mui/material/Slider';
import Accordion from '@mui/material/Accordion';
import Typography from '@mui/material/Typography';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import { FEATURE_FLAGS } from 'src/global-config';

import { Iconify } from 'src/components/iconify';

import ModelSelect from './SettingsModelSelect';
import SettingsConfirmDialog from './SettingsConfirmDialog';
import { getModelDetails } from '../context/SettingsContext';
import KnowledgeBaseSelect from './SettingsKnowledgeBaseSelect';
import { useSettingsContext } from '../hooks/useSettingsContext';
import { useChatHistoryContext } from '../hooks/useChatHistoryContext';

// Constants for slider and input configurations
const MAX_TOKENS_STEP = 10;
const MAX_TOP_K = 100;
const TOP_K_STEP = 1;
const MAX_TOP_P = 1;
const TOP_P_STEP = 0.1;
const MAX_TEMPERATURE = 1;
const TEMPERATURE_STEP = 0.1;
const MIN_VALUE = 0;

const sliderStyle = {
  height: 4,
  '& .MuiSlider-thumb': {
    color: '#5166FE',
    height: 14,
    width: 14,
  },
  '& .MuiSlider-track': {
    height: 4,
  },
  '& .MuiSlider-rail': {
    height: 4,
  },
};

export const StyledSettingsLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontWeight: 600,
  fontSize: '12px',
}));

function SettingsView() {
  const { settings, setSettings } = useSettingsContext();
  const { clearHistory, history } = useChatHistoryContext();
  const confirmDialog = useBoolean();

  const [newModel, setNewModel] = useState<string>(settings.model);

  // Common handler for numeric inputs with range validation
  const handleNumericInputChange =
    (field: keyof typeof settings, min: number, max: number, step: number = 1) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const parseFunc = step < 1 ? parseFloat : parseInt;
      const value = parseFunc(e.target.value);
      const clamped = Math.max(min, Math.min(max, value || 0));
      setSettings((prev) => ({ ...prev, [field]: clamped }));
    };

  const handleModelSelectConfirm = () => {
    setSettings((prev) => ({
      ...prev,
      model: newModel,
      maxTokens: getModelDetails(newModel).token_limit,
    }));
    clearHistory();
    confirmDialog.setFalse();
  };

  const handleModelSelect = (model: string) => {
    setNewModel(model);
    if (history.messages.length > 0) {
      confirmDialog.setTrue();
    } else {
      setSettings((prev) => ({ ...prev, model, maxTokens: getModelDetails(model).token_limit }));
    }
  };

  const modelDetails = useMemo(() => getModelDetails(newModel), [newModel]);

  return (
    <Stack pt={2} spacing={2} flexDirection="column">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight={600}>
          Run Settings
        </Typography>
      </Stack>
      <Stack gap={2.5}>
        <Stack gap={1}>
          <StyledSettingsLabel>Models</StyledSettingsLabel>
          <ModelSelect onChange={handleModelSelect} />
        </Stack>
        <SettingsConfirmDialog
          isOpen={confirmDialog.value}
          onClose={confirmDialog.setFalse}
          onConfirm={handleModelSelectConfirm}
        />
        {FEATURE_FLAGS.enableKnowledgeBase === 'true' && <KnowledgeBaseSelect />}
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            bgcolor: 'transparent',
            '&:before': { display: 'none' }, // Removes the divider
            boxShadow: 'none',
            border: 'none',
            '&.Mui-expanded': {
              margin: 0,
              boxShadow: 'none',
              bgcolor: 'transparent',
            },
            '& .MuiPaper-root': {
              boxShadow: 'none',
            },
          }}
        >
          <AccordionSummary
            sx={{
              p: 0,
              minHeight: 'auto',
              '&.Mui-expanded': {
                minHeight: 'auto',
              },
            }}
            expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
          >
            <Typography variant="body2" fontWeight={600}>
              Advance Settings
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: '8px 8px 0 0',
              bgcolor: 'transparent',
              boxShadow: 'none',
              border: 'none',
            }}
          >
            <Stack mb={2}>
              <Grid2 container alignItems="center">
                <Grid2 size={8}>
                  <StyledSettingsLabel>Max Tokens</StyledSettingsLabel>
                </Grid2>
                <Grid2 size={4}>
                  <Input
                    value={settings.maxTokens}
                    size="small"
                    onChange={handleNumericInputChange(
                      'maxTokens',
                      MIN_VALUE,
                      modelDetails?.token_limit,
                      MAX_TOKENS_STEP
                    )}
                    inputProps={{
                      type: 'number',
                      'aria-labelledby': 'max-tokens-slider-input',
                    }}
                  />
                </Grid2>
              </Grid2>
              <Slider
                value={settings.maxTokens}
                onChange={(_, newValue) => {
                  setSettings((prev) => ({ ...prev, maxTokens: newValue as number }));
                }}
                min={MIN_VALUE}
                max={modelDetails?.token_limit}
                step={MAX_TOKENS_STEP}
                sx={sliderStyle}
              />
            </Stack>
            <Stack mb={2}>
              <Grid2 container alignItems="center">
                <Grid2 size={8}>
                  <StyledSettingsLabel>Top K</StyledSettingsLabel>
                </Grid2>
                <Grid2 size={4}>
                  <Input
                    value={settings.topK}
                    size="small"
                    onChange={handleNumericInputChange('topK', MIN_VALUE, MAX_TOP_K, TOP_K_STEP)}
                    inputProps={{
                      type: 'number',
                      'aria-labelledby': 'top-k-slider-input',
                    }}
                  />
                </Grid2>
              </Grid2>
              <Slider
                value={settings.topK}
                onChange={(_, newValue) => {
                  setSettings((prev) => ({ ...prev, topK: newValue as number }));
                }}
                min={MIN_VALUE}
                max={MAX_TOP_K}
                step={TOP_K_STEP}
                sx={sliderStyle}
              />
            </Stack>
            <Stack mb={2}>
              <Grid2 container alignItems="center">
                <Grid2 size={8}>
                  <StyledSettingsLabel>Top P</StyledSettingsLabel>
                </Grid2>
                <Grid2 size={4}>
                  <Input
                    value={settings.topP}
                    size="small"
                    onChange={handleNumericInputChange('topP', MIN_VALUE, MAX_TOP_P, TOP_P_STEP)}
                    inputProps={{
                      type: 'number',
                      'aria-labelledby': 'top-p-slider-input',
                    }}
                  />
                </Grid2>
              </Grid2>
              <Slider
                value={settings.topP}
                onChange={(_, newValue) => {
                  setSettings((prev) => ({ ...prev, topP: newValue as number }));
                }}
                min={MIN_VALUE}
                max={MAX_TOP_P}
                step={TOP_P_STEP}
                sx={sliderStyle}
              />
            </Stack>
            <Stack mb={2}>
              <Grid2 container alignItems="center">
                <Grid2 size={8}>
                  <StyledSettingsLabel>Temperature</StyledSettingsLabel>
                </Grid2>
                <Grid2 size={4}>
                  <Input
                    value={settings.temperature}
                    size="small"
                    onChange={handleNumericInputChange(
                      'temperature',
                      MIN_VALUE,
                      MAX_TEMPERATURE,
                      TEMPERATURE_STEP
                    )}
                    inputProps={{
                      type: 'number',
                      'aria-labelledby': 'temperature-slider-input',
                    }}
                  />
                </Grid2>
              </Grid2>
              <Slider
                value={settings.temperature}
                onChange={(_, newValue) => {
                  setSettings((prev) => ({ ...prev, temperature: newValue as number }));
                }}
                min={MIN_VALUE}
                max={MAX_TEMPERATURE}
                step={TEMPERATURE_STEP}
                sx={sliderStyle}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Stack>
  );
}

export default SettingsView;
